import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  FileText,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('all');

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setInvoices([
        {
          id: 'INV-2024-001',
          clientName: 'محمد أحمد',
          clientType: 'فرد',
          serviceType: 'استشارة قانونية',
          status: 'paid',
          paymentMethod: 'بطاقة ائتمانية',
          issueDate: '2024-12-01',
          dueDate: '2024-12-15',
          totalAmount: 5000,
          remainingBalance: 0,
          caseId: 'WZ-2024-001'
        },
        {
          id: 'INV-2024-002',
          clientName: 'شركة النور التجارية',
          clientType: 'شركة',
          serviceType: 'خدمات قانونية شاملة',
          status: 'pending',
          paymentMethod: 'تحويل بنكي',
          issueDate: '2024-12-03',
          dueDate: '2024-12-20',
          totalAmount: 15000,
          remainingBalance: 15000,
          caseId: null
        },
        {
          id: 'INV-2024-003',
          clientName: 'فاطمة علي',
          clientType: 'فرد',
          serviceType: 'تمثيل قضائي',
          status: 'overdue',
          paymentMethod: 'نقداً',
          issueDate: '2024-11-15',
          dueDate: '2024-11-30',
          totalAmount: 8000,
          remainingBalance: 8000,
          caseId: 'WZ-2024-015'
        },
        {
          id: 'INV-2024-004',
          clientName: 'خالد محمود',
          clientType: 'فرد',
          serviceType: 'صياغة عقود',
          status: 'cancelled',
          paymentMethod: 'بطاقة ائتمانية',
          issueDate: '2024-11-20',
          dueDate: '2024-12-05',
          totalAmount: 3000,
          remainingBalance: 0,
          caseId: null
        }
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        label: 'مدفوعة',
        icon: CheckCircle,
        className: 'bg-green-50 text-green-700 border-green-200'
      },
      pending: {
        label: 'معلقة',
        icon: Clock,
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200'
      },
      overdue: {
        label: 'متأخرة',
        icon: AlertCircle,
        className: 'bg-red-50 text-red-700 border-red-200'
      },
      cancelled: {
        label: 'ملغاة',
        icon: XCircle,
        className: 'bg-gray-50 text-gray-700 border-gray-200'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: invoices.length,
    paid: invoices.filter(i => i.status === 'paid').length,
    pending: invoices.filter(i => i.status === 'pending').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    totalAmount: invoices.reduce((sum, i) => sum + i.totalAmount, 0),
    collected: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.totalAmount, 0),
    outstanding: invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled').reduce((sum, i) => sum + i.remainingBalance, 0)
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل الفواتير...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2">إدارة الفواتير</h1>
              <p className="text-gray-600">إدارة شاملة للفواتير والمدفوعات</p>
            </div>
            {hasPermission('finance.create') && (
              <button
                onClick={() => navigate('/admin/finance/invoices/create')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all"
              >
                <Plus className="w-5 h-5" />
                <span>إنشاء فاتورة جديدة</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الفواتير</p>
                <p className="text-2xl font-black text-[#1E3A8A]">{stats.total}</p>
              </div>
              <FileText className="w-10 h-10 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المبالغ المحصلة</p>
                <p className="text-2xl font-black text-green-600">{stats.collected.toLocaleString()} ر.س</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-yellow-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المبالغ المعلقة</p>
                <p className="text-2xl font-black text-yellow-600">{stats.outstanding.toLocaleString()} ر.س</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المتأخرة</p>
                <p className="text-2xl font-black text-red-600">{stats.overdue}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-red-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث برقم الفاتورة أو اسم العميل..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الحالات</option>
                <option value="paid">مدفوعة</option>
                <option value="pending">معلقة</option>
                <option value="overdue">متأخرة</option>
                <option value="cancelled">ملغاة</option>
              </select>
            </div>

            {/* Date Filter */}
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع التواريخ</option>
                <option value="today">اليوم</option>
                <option value="week">هذا الأسبوع</option>
                <option value="month">هذا الشهر</option>
                <option value="year">هذا العام</option>
              </select>
            </div>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">رقم الفاتورة</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">العميل</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">نوع الخدمة</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الحالة</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">تاريخ الإصدار</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">تاريخ الاستحقاق</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المبلغ</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المتبقي</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>لا توجد فواتير</p>
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-bold text-[#1E3A8A]">{invoice.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-semibold text-gray-800">{invoice.clientName}</p>
                          <p className="text-xs text-gray-500">{invoice.clientType}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{invoice.serviceType}</span>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(invoice.status)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{invoice.issueDate}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{invoice.dueDate}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-[#1E3A8A]">{invoice.totalAmount.toLocaleString()} ر.س</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-bold ${invoice.remainingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {invoice.remainingBalance.toLocaleString()} ر.س
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/finance/invoices/${invoice.id}`)}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                            title="عرض التفاصيل"
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </button>
                          {hasPermission('finance.edit') && (
                            <button
                              onClick={() => navigate(`/admin/finance/invoices/${invoice.id}/edit`)}
                              className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="تعديل"
                            >
                              <Edit className="w-4 h-4 text-yellow-600" />
                            </button>
                          )}
                          {hasPermission('finance.delete') && (
                            <button
                              onClick={() => {/* Handle delete */}}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                          <button
                            onClick={() => {/* Handle download */}}
                            className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                            title="تحميل"
                          >
                            <Download className="w-4 h-4 text-green-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default InvoicesPage;
